/*
 * Copyright (c) 2020.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Nikita Tropin <ntropin@yandex.ru>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {TuiTime} from '@taiga-ui/cdk';

import {ChangeDetectionStrategy, Component, ElementRef, EventEmitter, OnInit, ViewChild} from '@angular/core';

import {BaseComponent, ClosedPaymentService, CrmIssue, CrmJob, DateService, IssueType} from 'compob2b-libs';
import {tuiCreateTimePeriods} from '@taiga-ui/kit';

import {FormControl, FormGroup, Validators} from '@angular/forms';
import {addHours, addMinutes, differenceInMinutes, endOfDay, format, getHours, getMinutes} from 'date-fns';
import {distinctUntilChanged, map, takeUntil, tap} from 'rxjs/operators';
import {Router} from '@angular/router';
import {NbDialogRef} from '@nebular/theme';
import {translations} from '../../../../environments/translations.ru';
import {NgxDurationInputService} from '../../../admin-shared/services/ngx-duration-input.service';

@Component({
    templateUrl: './job-modal.component.html',
    styleUrls: ['./job-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class JobModalComponent extends BaseComponent implements OnInit {
  duration: number = 60;
  durationHeader = '';

  onAdd = new EventEmitter<string>();
  loading = true;
  date: Date;
  form: FormGroup;
  issue: CrmIssue = null;
  jobId: number;
  canRemoveTask = true;
  timePeriods = tuiCreateTimePeriods(8, 24);
  @ViewChild('diffTime') diffTime: ElementRef;

  constructor(protected readonly router: Router,
              private readonly closedPaymentService: ClosedPaymentService,
              private readonly dialogRef: NbDialogRef<any>,
              private durationService: NgxDurationInputService,
              private dateService: DateService) {
    super();
  }

  ngOnInit(): void {
    let minutes = getMinutes(this.date);
    if (minutes != 30) {
      minutes = 0;
    }

    this.form = new FormGroup({
      date: new FormControl(this.date ?? new Date(), Validators.required),
      startTime: new FormControl(new TuiTime(getHours(this.date), minutes), Validators.required),
      endTime: new FormControl(new TuiTime(getHours(addHours(this.date, 1)), minutes), Validators.required),
      content: new FormControl(),
      diffTime: new FormControl(),
    });

    this.form.get('startTime').valueChanges.pipe(takeUntil(this.unsubscribe), distinctUntilChanged(),).subscribe((term) => {
      if (!this.loading) {
        this.changeInputStart();
      }
    });
    this.form.get('endTime').valueChanges.pipe(takeUntil(this.unsubscribe), distinctUntilChanged(),).subscribe((term) => {
      if (!this.loading) {
        this.changeInputEnd();
      }
    });

    if (this.jobId) {
      this.canRemoveTask = false;
      this.loadJob(this.jobId);
    } else {
      this.setDuration(this.duration);
      this.onLoadDone();
    }
  }

  loadJob(id: number) {
    this.compoApiService.get<CrmJob>('/job/' + id).pipe(map(response => response.data)).pipe(tap(result => {
      const startAt = this.dateService.getShiftedTimeForOutput(new Date(result.startAt));
      const endAt = this.dateService.getShiftedTimeForOutput(new Date(result.endAt));

      this.issue = result.crmIssue;
      this.date = new Date(startAt);
      this.form.get('date').patchValue(this.date);
      this.form.get('startTime').patchValue(new TuiTime(getHours(startAt), getMinutes(startAt)));
      this.form.get('endTime').patchValue(new TuiTime(getHours(endAt), getMinutes(endAt)));
      this.form.get('content').patchValue(result.content);

      this.onLoadDone();
    }), takeUntil(this.unsubscribe)).subscribe();
  }

  onLoadDone() {
    this.changeInputEnd();
    this.loading = false;
  }

  save() {
    let data = {
      date: null,
      crmIssueId: this.issue.id,
      content: this.form.get('content').value,
      startHour: this.form.get('startTime').value.hours,
      startMinute: this.form.get('startTime').value.minutes,
      endHour: this.form.get('endTime').value.hours,
      endMinute: this.form.get('endTime').value.minutes
    };

    if (this.jobId) {
      data.date = format(this.form.get('date').value, 'yyyy-MM-dd');

      this.compoApiService.post('/job/update/' + this.jobId, data).pipe(takeUntil(this.unsubscribe)).subscribe(responce => {
        window.location.reload();
      });

    } else {
      data.date = format(endOfDay(this.form.get('date').value), 'yyyy-MM-dd');

      this.compoApiService.post('/job/save', data).pipe(takeUntil(this.unsubscribe)).subscribe(_ => {
        this.dialogRef.close();
        this.onAdd.emit('save');
      });
    }
  }

  setIssue(issue) {
    this.issue = <CrmIssue>issue;
  }

  changeDiffTime() {
    if (this.form.get('diffTime').valid) {
      this.duration = this.durationService.parseDurationString(this.form.get('diffTime').value);

      const startDate = this.getStartDate();
      const endDate = addMinutes(startDate, this.duration);

      if (!this.isValidEndTime(startDate, endDate)) {
        this.setEndTimeToZero(startDate, endDate);
      } else {
        this.form.get('endTime').patchValue(new TuiTime(getHours(endDate), getMinutes(endDate)));
      }
    }
  }

  changeInputStart() {
    const startDate = this.getStartDate();
    const endDate = addMinutes(startDate, this.duration);

    if (!this.isValidEndTime(startDate, endDate)) {
      this.setEndTimeToZero(startDate, endDate);
    } else {
      this.form.get('endTime').patchValue(new TuiTime(getHours(endDate), getMinutes(endDate)));
    }
  }

  changeInputEnd() {
    const startDate = this.getStartDate();
    const endHours = this.form.get('endTime').value.hours;
    const endMinutes = this.form.get('endTime').value.minutes;
    let endDate = new Date(this.date.getFullYear(), this.date.getMonth(), this.date.getDate(), endHours, endMinutes);

    if (!this.isValidEndTime(startDate, endDate)) {
      this.setDuration(60);
      endDate = addMinutes(startDate, this.duration);
      this.form.get('endTime').patchValue(new TuiTime(getHours(endDate), getMinutes(endDate)));
    } else if (endHours == 0 && endMinutes == 0) {
      this.setDuration((24 - startDate.getHours()) * 60 - startDate.getMinutes());
    } else {
      this.setDuration(differenceInMinutes(endDate, startDate));
    }
  }

  private isValidEndTime(startDate: Date, newEnd: Date) {
    const startMinutes = startDate.getHours() * 60 + startDate.getMinutes();
    const endMinutes = newEnd.getHours() * 60 + newEnd.getMinutes();
    return (startMinutes < endMinutes) || (!endMinutes && startMinutes);
  }

  private getStartDate() {
    const startHour = this.form.get('startTime').value.hours;
    const startMinute = this.form.get('startTime').value.minutes;

    return new Date(this.date.getFullYear(), this.date.getMonth(), this.date.getDate(), startHour, startMinute);
  }

  private setEndTimeToZero(startTime, endTime) {
    endTime.setHours(0);
    endTime.setMinutes(0);
    this.setDuration(differenceInMinutes(endTime, startTime));
    this.form.get('endTime').patchValue(new TuiTime(0, 0));
  }

  setDuration(value) {
    this.duration = value;
    this.durationHeader = this.durationService.getDurationString(this.duration);

    this.form.get('diffTime').patchValue(this.durationHeader);
  }

  isValid(): boolean {
    return !this.getLogtimeError() && this.form.valid && !!this.issue;
  }

  getLogtimeError(): string {
    if (this.issue) {
      if (this.issue.crmIssueType?.id === IssueType.EPIC || !!this.issue.epicName) {
        return 'Логирование в epic задачи запрещено';
      }

      return this.closedPaymentService.isIssuePaymentClosed(this.issue) ? translations.paymentClosedLogtime : '';
    }
  }
}
